<template>
  <div class="login-container">
    <div class="login-wrapper">
      <Head class="move-up" />

      <div class="fade-in">
        <button type="button" class="button-text language-wrapper" @click="toggleLangModal">
          <span v-html="t('login_language_to_interpret')" />
        </button>
        <FormLogin
          v-model:login-form="loginForm"
          v-model:error-login="errorLogin"
          :type-login="getLoginType()"
          @handle-login="handleLogin"
        />
        <ButtonProcess :loading="loading" full-width :disabled="isDisabledButtonLogin" @click="handleLogin">
          {{ t('login') }}
        </ButtonProcess>
        <p class="small dim">
          {{ t('input_login_comment') }}
        </p>
        <LoginBanner v-if="isVisibleBanner" />
      </div>
    </div>

    <teleport to="body">
      <ModalSelectLanguage v-if="showLangModal" @close="toggleLangModal" />
    </teleport>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import { requestAuthKey, requestLoginApp } from '@/api/apiService';
import FormLogin from '@/components/domains/chat/common/FormLogin.vue';
import Head from '@/components/domains/chat/common/Head.vue';
import ModalSelectLanguage from '@/components/domains/chat/common/ModalSelectLanguage.vue';
import LoginBanner from '@/components/domains/login/LoginBanner.vue';
import ButtonProcess from '@/components/shared/base/ButtonProcess.vue';
import { getLanguageByCode } from '@/static/languages';
import { useAudioStore } from '@/stores/audio';
import { useUserStore } from '@/stores/user';
import { LanguageCode, Nullable, TypeLogin, TypeLoginChannel, TypeUser } from '@/types/common';
import { IErrorLogin, ILoginForm } from '@/types/components';
import { IAppError } from '@/types/response';
import { encryptPassword } from '@/utils/encryptor';

const route = useRoute();
const router = useRouter();
const userStore = useUserStore();
const audioStore = useAudioStore();
const { t } = useI18n({ useScope: 'global' });

// 로그인 입력란
const loginForm = ref<ILoginForm>({
  userId: '',
  password: '',
  partnerCode: '',
  loginType: '',
});

const showLangModal = ref<boolean>(false);
const languageSelected = ref<Nullable<LanguageCode>>(null);

const loading = ref<boolean>(false);
const errorLogin = ref<IErrorLogin>();

// 로그인 버튼 비활성화 여부
const isDisabledButtonLogin = computed(() => {
  if (loading.value) return true;
  const { partnerCode } = loginForm.value;
  const isEmptyPartnerCode = partnerCode.trim() === '';
  return isEmptyPartnerCode;
});

// 배너 노출 여부
const isVisibleBanner = computed(() => {
  if (!route.query.referrer) return false;
  return route.query.referrer === 'https://www.sconchat.com/';
});

// 페이지 이동
const movePage = (userType: TypeUser) => {
  const {
    query: { type = '', redirectTo = '' },
  } = route;
  let path = '';

  if (redirectTo) {
    path = redirectTo.toString();
  } else if (type) {
    path =
      {
        admin: userType === 'HOST' ? '/admin/owner' : '/admin/employee',
        dist: '/dist/dist-dashboard',
        internal: '/internal/toolkit',
        // ss: '/ss/dashboard', TODO 아래 삭제 및 주석 해제
        ss: '/admin/employee',
      }[type.toString()] || '';
  }

  router.push(path);
};

/**
 * 로그인 경로 확인
 */
const isMatchLoginRoute = (key: string) => {
  const { type = '', redirectTo = '' } = route.query;
  return type === key || (redirectTo && redirectTo.includes(key));
};

const getLoginType = (): TypeLogin => {
  if (isMatchLoginRoute('dist')) return 'DISTRIBUTOR';
  if (isMatchLoginRoute('internal')) return 'HEAD_OFFICE';
  if (isMatchLoginRoute('admin') || loginForm.value.loginType === 'CUSTOMER') return 'CUSTOMER';

  //채팅방 진입 전, 로그인
  if (isMatchLoginRoute('chat') && route.query?.partnerType) return route.query.partnerType as TypeLogin;

  return 'CUSTOMER';
};

const getLoginChannel = (loginType: TypeLogin): TypeLoginChannel => {
  if (isMatchLoginRoute('chat')) return 'CHAT';
  return 'OPERATOR';
};

// 언어선택 초기화
const initLanguageSelected = () => {
  const selectedLanguage = audioStore.languageSelected || getLanguageByCode(navigator.language);
  // const locale = localStorage.getItem('locale');

  if (selectedLanguage) {
    languageSelected.value = selectedLanguage;
    // userStore.setPageLanguage(selectedLanguage.code);
  }

  // if (locale) {
  //   i18n.global.locale.value = locale;
  // }
};

// 비밀번호 암호화
const getEncryptedPassword = async (): Promise<string> => {
  localStorage.removeItem('SC-USER-ID');
  localStorage.removeItem('authToken');
  sessionStorage.removeItem('authToken');

  const { data: { partnerPublicKey = '' } = { partnerPublicKey: '' } } = await requestAuthKey();
  return await encryptPassword(partnerPublicKey, loginForm.value.password);
};

// 언어선택 모달 토글
const toggleLangModal = () => {
  showLangModal.value = !showLangModal.value;
};

// 에러메시지
const handleerrorLogin = (responseError: IAppError) => {
  let messageDisplayed = '';
  switch (responseError.errorCode) {
    case 1001:
      messageDisplayed = 'invalid_access';
      break;
    case 1002:
    case 1004:
      messageDisplayed = 'failed_to_login_by_id_or_pw';
      break;
    case 1008:
      messageDisplayed = 'invalid_access';
      break;
    case 1015:
      messageDisplayed = 'login_no_such_partner_code';
      break;
    default:
      messageDisplayed = 'failed_to_login_for_unknown_reason';
      break;
  }
  errorLogin.value = { ...responseError, messageDisplayed };
};

// 로그인
const handleLogin = async () => {
  loading.value = true;

  try {
    const loginType = getLoginType();
    const loginChannel = getLoginChannel(loginType);
    const partnerCode = loginForm.value.partnerCode;
    const password = await getEncryptedPassword();

    const { data, success } = await requestLoginApp(
      {
        userId: loginForm.value.userId,
        password,
      },
      { type: loginType, partnerCode, loginChannel }
    );

    if (success) {
      userStore.setLoginInfo({ ...data, loginType, partnerCode });
      await userStore.fetchUserInfo();
      movePage(data.userType);
    }
  } catch (error) {
    handleerrorLogin(error);
  }

  loading.value = false;
};

onMounted(() => {
  initLanguageSelected();
});
</script>

<style scoped lang="scss">
.login-container {
  display: flex;
  flex-direction: column;
  height: 100dvh;
  justify-content: center;
  align-items: center;
  margin: auto 1.5rem;
}

.login-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 25rem;
  margin-bottom: 5rem;
}

.language-wrapper {
  margin-bottom: 0.5rem;
}

.button-full-width {
  margin-top: 1rem;
  margin-bottom: 1rem;
}

@include respond-from(tablet) {
  .login-container {
    width: 100%;
    height: 100dvh;
    background: var(--surface-background);
    margin: unset;
  }

  .login-wrapper {
    width: 27rem;
    margin: 0 auto;
    padding: 2.5rem 2rem 2rem 2rem;
    border-radius: 8px;
    background-color: var(--surface-default);
    box-shadow: $shadow-modal;
  }
}
</style>
